import type { SupabaseClient } from '@supabase/supabase-js';

export interface EventTemplateCore {
    id?: string;
    user_id: string;
    name: string;
    notes?: string | null;
    use_count?: number;
    last_used_at?: string | null;
    custom_fields?: string[] | null;
    last_values?: Record<string, string> | null;
}

function normalize(t: any): EventTemplateCore {
    return {
        id: t.id,
        user_id: t.user_id,
        name: t.name,
        notes: t.notes ?? null,
        use_count: t.use_count ?? 0,
        last_used_at: t.last_used_at ?? null,
        custom_fields: Array.isArray(t.custom_fields) ? t.custom_fields : (t.custom_fields ?? null),
        last_values: (t.last_values ?? null) as any,
    };
}

export async function listEventTemplatesCore(sb: SupabaseClient, userId: string): Promise<EventTemplateCore[]> {
    const { data, error } = await sb
        .from('event_templates')
        .select('id,user_id,name,notes,use_count,last_used_at,custom_fields,last_values')
        .eq('user_id', userId)
        .order('use_count', { ascending: false })
        .order('last_used_at', { ascending: false });
    if (error) throw error;
    return (data || []).map(normalize);
}

export async function upsertEventTemplateCore(sb: SupabaseClient, userId: string, name: string, notes?: string | null): Promise<void> {
    const trimmed = (name || '').trim();
    if (!userId || !trimmed) return;
    const { data, error } = await sb
        .from('event_templates')
        .select('id,name,use_count')
        .eq('user_id', userId)
        .ilike('name', trimmed);
    if (error) throw error;
    const existing = (data || []).find((r: any) => String(r.name).toLowerCase() === trimmed.toLowerCase());
    if (existing) {
        const { error: upErr } = await sb
            .from('event_templates')
            .update({
                notes: notes ?? null,
                use_count: (existing.use_count || 0) + 1,
                last_used_at: new Date().toISOString(),
            })
            .eq('id', existing.id);
        if (upErr) throw upErr;
    } else {
        const { error: insErr } = await sb
            .from('event_templates')
            .insert({ user_id: userId, name: trimmed, notes: notes ?? null, use_count: 1, last_used_at: new Date().toISOString() });
        if (insErr) throw insErr;
    }
}

export async function upsertTemplateFieldsCore(
    sb: SupabaseClient,
    userId: string,
    name: string,
    fieldLabels: string[],
    lastValues?: Record<string, string>
): Promise<void> {
    const trimmed = (name || '').trim();
    if (!userId || !trimmed) return;
    const { data, error } = await sb
        .from('event_templates')
        .select('id,name,custom_fields,last_values')
        .eq('user_id', userId)
        .ilike('name', trimmed);
    if (error) throw error;
    const existing = (data || []).find((r: any) => String(r.name).toLowerCase() === trimmed.toLowerCase());
    if (existing) {
        const set = new Set<string>(Array.isArray(existing.custom_fields) ? existing.custom_fields : []);
        for (const f of fieldLabels) set.add(f);
        const { error: upErr } = await sb
            .from('event_templates')
            .update({ custom_fields: Array.from(set), last_values: lastValues ?? existing.last_values ?? {} })
            .eq('id', existing.id);
        if (upErr) throw upErr;
    } else {
        const { error: insErr } = await sb
            .from('event_templates')
            .insert({ user_id: userId, name: trimmed, custom_fields: fieldLabels, last_values: lastValues ?? {} });
        if (insErr) throw insErr;
    }
}
